import React, { useEffect, useState, useRef } from "react";
import { api } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { MapPin, ExternalLink, Clock, Shield, Check, X, Utensils, Coffee, LogIn, LogOut } from "lucide-react";
import { MapContainer, TileLayer, Marker, Circle, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import WorkModeBadge from "./WorkModeBadge";

// Fix Leaflet default icon paths (CRA breaks them)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

// Custom QR icon (orange)
const qrIcon = L.divIcon({
  html: `<div style="background:#FF7A29;border:3px solid #fff;border-radius:50%;width:24px;height:24px;box-shadow:0 2px 8px rgba(0,0,0,.4);display:flex;align-items:center;justify-content:center"><div style="background:#fff;border-radius:50%;width:8px;height:8px"></div></div>`,
  className: "qr-icon",
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

const KIND_LABEL = { in: "Entrata", out: "Uscita", lunch_start: "Pausa pranzo", lunch_end: "Fine pausa" };
const KIND_ICON = { in: LogIn, out: LogOut, lunch_start: Utensils, lunch_end: Coffee };

export default function AttendanceMapDialog({ att, onClose }) {
  const [address, setAddress] = useState(null);
  const [loading, setLoading] = useState(true);
  const mapRef = useRef(null);

  useEffect(() => {
    let on = true;
    if (!att) return;
    setLoading(true);
    setAddress(null);
    api.post("/attendances/reverse-geocode", { latitude: att.latitude, longitude: att.longitude })
      .then(r => { if (on) setAddress(r.data); })
      .catch(() => { if (on) setAddress({ display_name: `${att.latitude.toFixed(6)}, ${att.longitude.toFixed(6)}` }); })
      .finally(() => { if (on) setLoading(false); });
    return () => { on = false; };
  }, [att]);

  if (!att) return null;

  const lat = att.latitude;
  const lng = att.longitude;
  const qrLat = att.qr_latitude;
  const qrLng = att.qr_longitude;
  const radius = att.qr_radius_m || 100;

  // Center: midpoint between user and QR if QR present, else user
  let center = [lat, lng];
  let zoom = 17;
  if (qrLat != null && qrLng != null) {
    center = [(lat + qrLat) / 2, (lng + qrLng) / 2];
    // Zoom adapts to distance
    const dist = att.distance_m || 0;
    if (dist > 2000) zoom = 12;
    else if (dist > 500) zoom = 14;
    else if (dist > 100) zoom = 16;
    else zoom = 17;
  }

  const osmLink = `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lng}#map=18/${lat}/${lng}`;
  const gmapsLink = `https://www.google.com/maps?q=${lat},${lng}`;
  const Icon = KIND_ICON[att.kind] || Clock;

  return (
    <Dialog open={!!att} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="rounded-2xl max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="attendance-map-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <MapPin className="w-6 h-6 text-[#FF7A29]"/> Posizione timbratura
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          {/* Map */}
          <div className="rounded-xl overflow-hidden border-2 border-[#EDE5D8]" style={{ height: 320 }}>
            <MapContainer
              center={center}
              zoom={zoom}
              style={{ height: "100%", width: "100%" }}
              data-testid="attendance-leaflet-map"
              ref={mapRef}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {/* Cerchio raggio QR */}
              {qrLat != null && qrLng != null && (
                <>
                  <Circle
                    center={[qrLat, qrLng]}
                    radius={radius}
                    pathOptions={{
                      color: att.valid ? "#10b981" : "#dc2626",
                      fillColor: att.valid ? "#10b981" : "#dc2626",
                      fillOpacity: 0.10,
                      weight: 2,
                    }}
                  />
                  <Marker position={[qrLat, qrLng]} icon={qrIcon}>
                    <Popup><strong>Sede QR</strong><br/>{att.qr_label || "Posizione QR"}<br/>Raggio: {radius}m</Popup>
                  </Marker>
                </>
              )}
              {/* Marker posizione timbratura */}
              <Marker position={[lat, lng]}>
                <Popup><strong>{KIND_LABEL[att.kind] || att.kind}</strong><br/>{new Date(att.timestamp).toLocaleString("it-IT")}<br/>{att.user_full_name || ""}</Popup>
              </Marker>
            </MapContainer>
          </div>

          {/* Legenda */}
          {qrLat != null && (
            <div className="flex flex-wrap items-center gap-3 text-xs text-[#555] px-1" data-testid="attendance-map-legend">
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-[#FF7A29] border-2 border-white shadow"></span> Sede QR</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-blue-500 border-2 border-white shadow"></span> Posizione timbratura</span>
              <span className="flex items-center gap-1.5">
                <span className={`w-3 h-3 rounded-full border-2 ${att.valid ? "border-emerald-500 bg-emerald-500/20" : "border-red-500 bg-red-500/20"}`}></span>
                Area consentita ({radius}m)
              </span>
            </div>
          )}

          {/* Meta */}
          <div className="grid grid-cols-2 gap-2">
            <div className="rounded-xl border border-[#EDE5D8] p-3">
              <div className="text-[10px] uppercase tracking-widest text-[#999] font-bold">Tipo</div>
              <div className="font-semibold flex items-center gap-1.5 mt-1">
                <Icon className="w-4 h-4 text-[#FF7A29]"/>
                {KIND_LABEL[att.kind] || att.kind}
              </div>
            </div>
            <div className="rounded-xl border border-[#EDE5D8] p-3">
              <div className="text-[10px] uppercase tracking-widest text-[#999] font-bold">Data e ora</div>
              <div className="font-semibold mt-1 text-sm">{new Date(att.timestamp).toLocaleString("it-IT")}</div>
            </div>
            <div className="rounded-xl border border-[#EDE5D8] p-3">
              <div className="text-[10px] uppercase tracking-widest text-[#999] font-bold">Stato GPS</div>
              <div className={`font-semibold mt-1 flex items-center gap-1.5 text-sm ${att.valid ? "text-emerald-700" : "text-red-700"}`}>
                {att.valid ? <Check className="w-4 h-4"/> : <X className="w-4 h-4"/>}
                {att.valid ? "Valida (in area)" : "Fuori area"}
              </div>
            </div>
            <div className="rounded-xl border border-[#EDE5D8] p-3">
              <div className="text-[10px] uppercase tracking-widest text-[#999] font-bold">Distanza dal QR</div>
              <div className="font-semibold mt-1 text-sm">{att.distance_m != null ? `${Math.round(att.distance_m)}m` : "—"}</div>
            </div>
          </div>

          {/* Address */}
          <div className="rounded-xl bg-[#FFFAF5] border-2 border-[#FF7A29]/30 p-3" data-testid="attendance-address-box">
            <div className="text-[10px] uppercase tracking-widest text-[#FF7A29] font-bold mb-1">Indirizzo rilevato</div>
            {loading ? (
              <div className="text-sm text-[#757575] animate-pulse">Risoluzione indirizzo in corso…</div>
            ) : (
              <div>
                <div className="text-sm font-semibold text-[#0A0A0A]" data-testid="attendance-address">{address?.display_name || "Indirizzo non disponibile"}</div>
                <div className="text-xs text-[#757575] mt-1 font-mono">{lat.toFixed(6)}, {lng.toFixed(6)} · QR: {att.qr_label || "—"}</div>
              </div>
            )}
          </div>

          {att.user_full_name && (
            <div className="text-sm flex items-center gap-2 text-[#555]">
              <Shield className="w-4 h-4 text-[#FF7A29]"/> Dipendente: <strong className="text-[#0A0A0A]">{att.user_full_name}</strong>
            </div>
          )}

          {(att.work_mode && att.work_mode !== "office") || att.note ? (
            <div className="rounded-xl border-2 border-amber-300 bg-amber-50 p-3 space-y-2" data-testid="attendance-work-mode-box">
              {att.work_mode && (
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-[10px] uppercase tracking-widest text-amber-800 font-bold">Modalità dichiarata</span>
                  <WorkModeBadge mode={att.work_mode}/>
                </div>
              )}
              {att.note && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-amber-800 font-bold mb-0.5">Nota dipendente</div>
                  <div className="text-sm text-[#0A0A0A] italic">“{att.note}”</div>
                </div>
              )}
            </div>
          ) : null}
        </div>

        <DialogFooter className="gap-2 flex-col sm:flex-row">
          <Button variant="ghost" onClick={onClose} className="rounded-full" data-testid="attendance-close">Chiudi</Button>
          <a href={osmLink} target="_blank" rel="noreferrer">
            <Button variant="outline" className="rounded-full w-full" data-testid="attendance-open-osm">
              <ExternalLink className="w-4 h-4 mr-1.5"/> OpenStreetMap
            </Button>
          </a>
          <a href={gmapsLink} target="_blank" rel="noreferrer">
            <Button className="rounded-full bg-[#FF7A29] hover:bg-[#E85D00] w-full" data-testid="attendance-open-gmaps">
              <ExternalLink className="w-4 h-4 mr-1.5"/> Google Maps
            </Button>
          </a>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
